import { DomainType } from "../Types";
import { GenericDomainDetail } from "../UI/Details";
import GenericDomainForm from "../UI/Forms/GenericDomainForm";
import { GenericDomainLayout } from "../UI/Layouts";
import { DOMAIN_CONFIGS } from "./domain-config";

/**
 * =====================================================
 * ENTITY_ROUTES
 * =====================================================
 *
 * Dynamically generates the route configuration for all domains
 * defined in `DOMAIN_CONFIGS`.
 *
 * Responsibilities:
 * - Maps each domain type to a route definition including:
 *    - path: URL path for the domain
 *    - role: minimum required role to access the domain
 *    - layout: component used as the main layout for the domain
 *    - detail: component used for detail view of a single entity
 *    - form: optional form component for creating/editing entities
 *    - hasFormSubType: boolean flag for domain forms with subtypes
 *    - domainType: the domain key
 * - Uses `GenericDomainLayout` and `GenericDomainDetail` as fallbacks
 *   if the domain does not have custom layout/detail components.
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */
export const ENTITY_ROUTES =  (Object.keys(DOMAIN_CONFIGS) as DomainType[]).map(domainType => {
	
	const config = DOMAIN_CONFIGS[domainType];
	
	// Layout component: use domain-specific layout if defined, otherwise fallback
	const LayoutComponent: React.FC = () =>
		config.layout ? <config.layout /> : <GenericDomainLayout domainType={domainType} />;

	
	// Detail component: use domain-specific detail if defined, otherwise fallback
	const DetailComponent: React.FC = () =>
		config.detail ? <config.detail /> : <GenericDomainDetail domainType={domainType} />;

	const FormComponent: React.FC = () => {
		if(!config.DomainForm) {
			return null;
		}
		return config.useFormHook ? <GenericDomainForm domainType={domainType} />  : <></>;
	}

	return {
		path: config.domainRoute,
		role: config.requireRole,
		Layout: LayoutComponent,
		Detail: DetailComponent,
		Form: FormComponent,
		FormSelector : config.DomainFormSelector,
		domainType
	};
});